import { apiCall } from './api.service';

export const getPayments = async (token, organisationId, paymentType, page, limit, filtering) => {
    const method = 'get';
    const path = `api/v1/organisations/${organisationId}/payments`;

    const params = {
        ...(paymentType && { status: paymentType }),
        ...(page && { page }),
        ...(limit && { limit }),
        ...(filtering && {
            ...(filtering.paymentMethod && { paymentMethod: filtering.paymentMethod }),
            ...(filtering.fromDate && { fromDate: filtering.fromDate }),
            ...(filtering.toDate && { toDate: filtering.toDate }),
            ...(filtering.search && { search: filtering.search }),
            ...(typeof filtering.intercompanyTransfer === 'boolean' && { intercompanyTransfer: filtering.intercompanyTransfer }),
            ...(filtering.includeLogs && { includeLogs: true }),
        }),

    };

    const response = await apiCall(method, {}, path, token, params);
    return response;
};

/**
 * Fetches one page of payments across multiple organisations via the aggregate
 * endpoint. Each payment is annotated with `_organisationId` and
 * `_organisationName` so the multi-org list can show (and deep-link to) the
 * owning organisation.
 *
 * @param {string} token
 * @param {Array<{ id: string|number, name?: string }>} organisations
 * @param {string} paymentType
 * @param {number} page
 * @param {number} limit
 * @param {object} [filtering]
 * @returns {Promise} axios-style response from the aggregate payments API
 */
export const getPaymentsForAllOrganisations = async (
    token,
    organisations,
    paymentType,
    page,
    limit,
    filtering
) => {
    const organisationIds = (organisations || [])
        .map((org) => org.id)
        .filter((id) => id != null)
        .join(',');

    const method = 'get';
    const path = 'api/v1/payments';
    const params = {
        organisationIds,
        ...(paymentType && { status: paymentType }),
        ...(page && { page }),
        ...(limit && { limit }),
        ...(filtering && {
            ...(filtering.paymentMethod && { paymentMethod: filtering.paymentMethod }),
            ...(filtering.fromDate && { fromDate: filtering.fromDate }),
            ...(filtering.toDate && { toDate: filtering.toDate }),
            ...(filtering.search && { search: filtering.search }),
            ...(typeof filtering.intercompanyTransfer === 'boolean' && { intercompanyTransfer: filtering.intercompanyTransfer }),
            ...(filtering.includeLogs && { includeLogs: true }),
        }),
    };

    const response = await apiCall(method, {}, path, token, params);

    if (!(response?.data?.success && Array.isArray(response.data.data))) {
        return response;
    }

    const orgById = new Map(
        (organisations || []).map((org) => [String(org.id), org])
    );

    // The aggregate payload already includes organisationId (integer PK as a
    // string). Map that onto the _organisation* fields the multi-org table uses.
    response.data.data = response.data.data.map((payment) => {
        const org = orgById.get(String(payment.organisationId));
        return {
            ...payment,
            _organisationId: org?.id ?? payment.organisationId,
            _organisationName: org?.name,
        };
    });

    return response;
};

export const getPaymentMethods = async (token, organisationId) => {
    const method = 'get';
    const path = `api/v1/organisations/${organisationId}/payments/methods`;

    const response = await apiCall(method, {}, path, token);
    return response;
};

export const getPaymentDetails = async (token, paymentId, organisationId) => {
    const method = 'get';
    const path = `api/v1/organisations/${organisationId}/payments/${paymentId}`;

    const response = await apiCall(method, {}, path, token);
    return response;
};

export const authorizePayment = async (token, paymentId, otp, organisationId, isRejected = false, reason = undefined) => {
    const method = 'post';
    const body = {
        organisationId,
        isRejected,
        // OTP is only required for AUTHORISER approvals; omit when empty (REVIEWER / rejections).
        ...(otp ? { otp } : {}),
        // A reason is mandatory when rejecting; logged & stored server-side.
        ...(isRejected && reason ? { reason } : {}),
    };
    const path = `api/v1/organisations/${organisationId}/payments/${paymentId}/authorise`;

    const response = await apiCall(method, body, path, token);
    return response;
};

export const getPaymentLogs = async (token, paymentId, organisationId) => {
    const method = 'get';
    const path = `api/v1/organisations/${organisationId}/payments/${paymentId}/logs`;

    const response = await apiCall(method, {}, path, token);
    return response;
};

const EXPORT_PAGE_SIZE = 100;
const EXPORT_MAX_PAGES = 100;

/**
 * Pages through the payments API until every matching row has been collected
 * (or the safety cap is hit). Used by the payments export dialog.
 *
 * @param {object} options
 * @param {string} options.token
 * @param {string|number} [options.organisationId]
 * @param {Array<{ id: string|number, name?: string }>} [options.organisations]
 * @param {boolean} [options.viewAllOrgs]
 * @param {string} [options.paymentType]
 * @param {object} [options.filtering]
 * @param {(progress: { page: number, lastPage: number, count: number }) => void} [options.onProgress]
 * @returns {Promise<{ payments: Array, truncated: boolean }>}
 */
export const fetchAllPaymentsForExport = async ({
    token,
    organisationId,
    organisations,
    viewAllOrgs,
    paymentType,
    filtering,
    onProgress,
}) => {
    const payments = [];
    let page = 1;
    let lastPage = 1;
    let truncated = false;

    while (page <= EXPORT_MAX_PAGES) {
        const response = viewAllOrgs
            ? await getPaymentsForAllOrganisations(
                token,
                organisations,
                paymentType,
                page,
                EXPORT_PAGE_SIZE,
                { ...filtering, includeLogs: true }
            )
            : await getPayments(
                token,
                organisationId,
                paymentType,
                page,
                EXPORT_PAGE_SIZE,
                { ...filtering, includeLogs: true }
            );

        if (!(response?.data?.success && Array.isArray(response.data.data))) {
            const message =
                response?.data?.error?.message
                || response?.statusText
                || 'Failed to fetch payments for export';
            throw new Error(message);
        }

        payments.push(...response.data.data);
        lastPage = response.data.meta?.pageInfo?.lastPage || page;
        const hasNextPage = Boolean(response.data.meta?.pageInfo?.hasNextPage);

        onProgress?.({ page, lastPage, count: payments.length });

        if (!hasNextPage || response.data.data.length === 0) {
            break;
        }

        if (page >= EXPORT_MAX_PAGES) {
            truncated = true;
            break;
        }

        page += 1;
    }

    return { payments, truncated };
};
